"use client";

import { useState, useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

interface Stat {
  id: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

export default function ImpactStatistics() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasAnimated(true);
            animateCounters();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isHydrated, hasAnimated]);

  const stats: Stat[] = [
    {
      id: "stat_workers",
      value: 5000,
      suffix: "+",
      label: "Fair Wage Workers",
      icon: "UsersIcon",
    },
    {
      id: "stat_schools",
      value: 50,
      suffix: "+",
      label: "Schools Built",
      icon: "AcademicCapIcon",
    },
    {
      id: "stat_traceable",
      value: 100,
      suffix: "%",
      label: "Supply Chain Traceable",
      icon: "ShieldCheckIcon",
    },
    {
      id: "stat_communities",
      value: 120,
      suffix: "+",
      label: "Communities Supported",
      icon: "HomeModernIcon",
    },
  ];

  const animateCounters = () => {
    stats.forEach((stat) => {
      const element = document.getElementById(`counter-${stat.id}`);
      if (!element) return;

      const duration = 2000;
      const startTime = performance.now();
      const startValue = 0;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOut = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.floor(startValue + (stat.value - startValue) * easeOut);

        element.textContent = currentValue.toString();

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          element.textContent = stat.value.toString();
        }
      };

      requestAnimationFrame(animate);
    });
  };

  return (
    <section
      ref={sectionRef}
      id="sustainability"
      className="py-24 bg-primary text-primary-foreground relative overflow-hidden"
    >
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center items-center gap-4 mb-6">
            <div className="w-[2px] h-4 bg-accent"></div>
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-primary-foreground/80 font-mono">
              Our Impact
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight mb-4 font-heading">
            Making a Real Difference
          </h2>
          <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto font-sans">
            Measurable positive impact on communities, environment, and workers.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="text-center group"
            >
              <div className="w-20 h-20 bg-primary-foreground/10 backdrop-blur-sm rounded-full flex items-center justify-center text-accent mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Icon name={stat.icon as any} size={40} />
              </div>

              <div className="text-5xl md:text-6xl font-bold mb-2 font-heading">
                <span id={`counter-${stat.id}`}>0</span>
                <span>{stat.suffix}</span>
              </div>

              <p className="text-sm uppercase tracking-widest text-primary-foreground/80 font-mono">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}